import { translate } from 'localizations';
import { FC, useEffect, useState } from 'react';
import { CustomModal } from 'shared/ui/CustomModal';
import { ContainedSelectWithLabel, CustomCheckbox } from 'components/common';
import { gptAPI } from 'services/gptService';
import { useAppSelector } from 'hooks/redux';
import { IconQuestion, NoDataIcon, UniversalButton } from 'shared/ui';
import variables from 'styles/variables.scss';
import { PlusIcon } from 'components/Icons/PlusIcon';
import DeleteColumnBtn from 'pages/Reports/ReportsComponents/components/DeleteColumnBtn/DeleteColumnBtn';
import TooltipCustom from 'components/common/Tooltip/Tooltip';
import { selectMenuPortalStyles } from 'hooks/useFormFields';
import { UserType } from 'store/user/user.types';
import { Skeleton } from '@mui/material';
import { useSnackbar } from 'notistack';
import { createSnackbarOptions } from 'components/common/Snackbar';
import { OnActionClickOptions } from '../CallsHeader/components/CallHeadersSettings/hooks/useActionsFunctions';
import s from './styles.module.scss';
import ContentSubmit from './ContentSubmit';

interface ApplyGPTModalProps {
	isOpen: boolean;
	language: string;
	count: number;
	onCancel: () => void;
	onEnter: (params: OnActionClickOptions) => void;
	user: UserType | null;
}

const ApplyGPTModal: FC<ApplyGPTModalProps> = ({ isOpen, language, count, onCancel, onEnter, user }) => {
	const { enqueueSnackbar } = useSnackbar();

	// первое дефолтное правило
	const [selectedGptRule, setSelectedGptRule] = useState<{ value: string; label: string } | null>(null);
	// добавочные правила
	const [selectedGptRulesAdditional, setSelectedGptRulesAdditional] = useState<{ value: string; label: string }[]>(
		[],
	);
	// проверка на выбранное первое правило
	const [isContentVerify, setIsContentVerify] = useState(false);

	const [gptRulesConverted, setGptRulesConverted] = useState<
		{ value: string | undefined; label: string | undefined }[]
	>([]);

	const [checked, setChecked] = useState(false);
	const onChangeChecked = () => {
		setChecked(!checked);
	};

	const [getGPTRules, { error: errorRules, isLoading: isLoadingRules }] = gptAPI.useLazyGetGPTRulesQuery();
	const fetchGptRules = async () => {
		await getGPTRules();
	};
	const { gptRules } = useAppSelector((state) => state.gpt);

	const handlePlus = () => {
		setSelectedGptRulesAdditional([...selectedGptRulesAdditional, { value: '', label: '' }]);
	};

	const onChangeGptRulesAdditional = (value: { value: string; label: string }, index: number) => {
		setSelectedGptRulesAdditional(
			selectedGptRulesAdditional.map((rule, indexRule) => (indexRule === index ? value : rule)),
		);
	};

	const onDeleteGptRulesAdditional = (value: { value: string; label: string }) => {
		setSelectedGptRulesAdditional(selectedGptRulesAdditional.filter((rule) => rule.value !== value.value));
	};

	const onChangeGptRule = (value: { value: string; label: string }) => {
		setSelectedGptRule(value);
	};

	const convertValues = () => {
		let rules = [];
		if (selectedGptRule) rules.push(selectedGptRule);
		if (selectedGptRulesAdditional.length) {
			for (let i = 0; i < selectedGptRulesAdditional.length; i++) {
				if (selectedGptRulesAdditional[i].value) rules.push(selectedGptRulesAdditional[i]);
			}
		}
		return {
			action: 'apply_gpt',
			rules: rules.map((rule) => rule.value),
			force_resend: checked,
		};
	};

	const handleEnter = () => {
		// если сообщение уже есть
		if (isContentVerify) {
			setIsContentVerify(false);
			onEnter(convertValues());
			onCancel();
		}
		// проверка на хотя бы одно выбранное правило
		if (!selectedGptRule) {
			setIsContentVerify(true);
			return;
		}
		// если выбрано хоть одно правило
		setIsContentVerify(false);
		onEnter(convertValues());
		onCancel();
	};

	const handleCancel = () => {
		if (isContentVerify) setIsContentVerify(false);
		else onCancel();
	};

	// динамические селекты
	const getAvailableOptionsForMain = () => {
		const selectedValues = selectedGptRulesAdditional.map((rule) => rule.value);
		return gptRulesConverted.filter((opt) => !selectedValues.includes(opt.value as string));
	};

	const getAvailableOptionsForAdditional = (currentIndex: number) => {
		const selectedValues = [
			selectedGptRule?.value,
			...selectedGptRulesAdditional.filter((_, i) => i !== currentIndex).map((rule) => rule.value),
		];
		return gptRulesConverted.filter((opt) => !selectedValues.includes(opt.value));
	};

	// запрос за правилами
	useEffect(() => {
		if (user) fetchGptRules();
	}, [user]);
	// приводим данные к нужному виду
	useEffect(() => {
		if (gptRules.length && gptRules[0] !== null) {
			setGptRulesConverted(gptRules.map((gptRule) => ({ label: gptRule?.title, value: gptRule?.id })));
		}
	}, [gptRules]);

	useEffect(() => {
		if (errorRules) {
			enqueueSnackbar(
				null,
				createSnackbarOptions({
					type: 'error',
					text: translate('checklists_error', language),
					time: 2000,
				}),
			);
		}
	}, [errorRules]);

	const isContentEmpty = gptRules?.length === 0;
	const isOkDisabled =
		!Boolean(gptRulesConverted.length) || selectedGptRulesAdditional?.[selectedGptRulesAdditional.length - 1]
			? !selectedGptRulesAdditional?.[selectedGptRulesAdditional.length - 1]?.value
			: false;

	return (
		<CustomModal
			destroyOnClose
			width="middle"
			open={isOpen}
			onCancel={handleCancel}
			isLoading={isLoadingRules}
			onOk={handleEnter}
			okText={translate('apply', language)}
			isOkDisabled={isOkDisabled}
			title={translate('applyGPTToCommunications', language)}
			aboveFooter={
				!(isContentEmpty || errorRules) &&
				!isContentVerify && (
					<div className={s.resendButton}>
						<CustomCheckbox
							testid="forceResendBtn"
							id="forceResendBtn"
							checked={checked}
							onChange={onChangeChecked}
							label={translate('applyGPTResend', language)}
							rootClassname={s.renewButton}
							labelStyle={{ fontWeight: '400', color: variables.gray_9 }}
						/>
						<TooltipCustom
							background="dark"
							title={translate('applyGPTResendHint', language)}
							placement="top"
							followCursor={false}
						>
							<IconQuestion />
						</TooltipCustom>
					</div>
				)
			}
		>
			{isContentEmpty || errorRules ? (
				<div className={s.empty}>
					<NoDataIcon />
					<div className={s.empty__title}>{translate('applyGPTEmpty', language)}</div>
				</div>
			) : (
				<>
					{isContentVerify ? (
						<ContentSubmit
							title={translate('applyGPTConfirm', language)}
							count={`${count} ${translate('uploadPiece', language)}`}
						/>
					) : (
						<>
							{isLoadingRules ? (
								<>
									<Skeleton variant="text" width={150} height={36} />
									<Skeleton variant="text" width={250} height={58} />
									<Skeleton variant="text" width={150} height={50} />
								</>
							) : (
								<div>
									<ContainedSelectWithLabel
										width="100%"
										isSearchable
										value={selectedGptRule}
										options={getAvailableOptionsForMain()}
										placeholder={translate('applyGPTAllRules', language)}
										title={translate('selectRule', language)}
										onSelectChange={onChangeGptRule}
										classNameRoot={s.selectInput}
										classNameLabel={s.selectInput__label}
										noOptionsMessage={translate('listEmpty', language)}
										{...selectMenuPortalStyles}
									/>
									{selectedGptRulesAdditional?.map((rule, index) => {
										return (
											<div className={s.additionalRule} key={rule.value ? rule.value : index}>
												<ContainedSelectWithLabel
													width="100%"
													isSearchable
													value={rule}
													options={getAvailableOptionsForAdditional(index)}
													placeholder={translate('titleRule', language)}
													title={translate('selectRule', language)}
													onSelectChange={(value) => onChangeGptRulesAdditional(value, index)}
													classNameRoot={s.additionalRuleSelect}
													classNameLabel={s.selectInput__label}
													noOptionsMessage={translate('listEmpty', language)}
													{...selectMenuPortalStyles}
												/>
												<DeleteColumnBtn
													testId="deleteRuleBtn"
													handleDeleteColumn={() => onDeleteGptRulesAdditional(rule)}
												/>
											</div>
										);
									})}
									<UniversalButton
										testId="addRuleBtn"
										buttonStyle="plain"
										disabled={
											selectedGptRulesAdditional.length > 0
												? Boolean(
														getAvailableOptionsForAdditional(
															selectedGptRulesAdditional.length - 1,
														).length === 1,
												  )
												: !selectedGptRule
										}
										className={s.addRuleButton}
										text={translate('applyGPTAddRule', language)}
										icon={<PlusIcon fill={variables.purple_5} />}
										handleClick={handlePlus}
									/>
								</div>
							)}
						</>
					)}
				</>
			)}
		</CustomModal>
	);
};

export default ApplyGPTModal;
